// Helpers for producing downloadable text / JSON the admin can send to a user
// before applying destructive changes. Mirrors the affordances the old folder-
// repair modal had ("Download for user", "Download JSON").

import type { AuditDocRef, Diagnosis, RestoreStructureResult, UploadedBackupPayload } from "../../api/repair";
import type { PlanStep, UploadedBackup } from "./repairReducer";

/**
 * Formats a backup timestamp label. Accepts ISO strings (renders as local time)
 * or pass-through strings like "upload: foo.json".
 */
export function formatBackupTime(value: string | undefined | null): string {
  if (!value) return "unknown time";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function downloadText(filename: string, content: string, mime = "text/plain") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function parseUploadedBackup(file: File, expectedProjectId: string): Promise<UploadedBackup> {
  const text = await file.text();
  const parsed = JSON.parse(text);
  if (!parsed || !Array.isArray(parsed.contents)) {
    throw new Error("Invalid backup JSON — missing contents array");
  }
  if (parsed.projectId && parsed.projectId !== expectedProjectId) {
    throw new Error(`This backup is for a different project (${parsed.projectId})`);
  }
  const contents = parsed.contents;
  const folders = Array.isArray(parsed.folders) ? parsed.folders : [];
  const payload: UploadedBackupPayload = { projectId: parsed.projectId, contents, folders };
  return {
    name: file.name,
    folderCount: folders.length,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    documentCount: contents.filter((i: any) => i && i.type === "document").length,
    payload,
  };
}

export function buildDiagnosisReport(
  projectTitle: string | undefined,
  projectId: string,
  diagnosis: Diagnosis,
  plan: PlanStep[],
  folderStepResult?: RestoreStructureResult | null
): string {
  const missing = diagnosis.missingFromTree;
  const external = diagnosis.externalDocs;
  const trash = diagnosis.trashedDocs;
  const backup = diagnosis.folders.backup;
  const lines: string[] = [];
  lines.push(`Sudowrite Repair Diagnosis`);
  lines.push(`Project: ${projectTitle || "Untitled"} (${projectId})`);
  lines.push(`Audited at: ${diagnosis.auditedAt}`);
  lines.push("");
  lines.push(`Summary`);
  lines.push(`  ${missing.length} missing from tree`);
  lines.push(`  ${external.length} external (contaminating)`);
  lines.push(`  ${trash.length} in trash`);
  lines.push(`  ${diagnosis.folders.count} folders currently in project`);
  if (backup.available) {
    lines.push(`  Backup: ${backup.folderCount} folders, ${backup.documentCount} documents from ${backup.readTime}`);
  } else {
    lines.push(`  Backup: unavailable at probed timestamp`);
  }
  lines.push("");

  const listSection = (title: string, rows: { id: string; title: string }[]) => {
    lines.push(title);
    if (rows.length === 0) {
      lines.push("  (none)");
    } else {
      for (const r of rows) lines.push(`  - ${r.title} (${r.id})`);
    }
    lines.push("");
  };

  listSection("Documents missing from tree (will be restored to root):", missing);
  listSection("External documents (will be removed):", external);
  listSection("Documents in trash (informational — not touched by repair):", trash);

  lines.push(`Planned steps`);
  for (const step of plan) {
    lines.push(`  [${step.status}] ${step.title}`);
    lines.push(`    ${step.description}`);
  }

  // If a backup has been picked and the dry-run has run, append the full
  // post-repair tree — that's the picture a user receiving the report
  // actually cares about.
  if (folderStepResult) {
    lines.push("");
    lines.push(`## Full repaired file tree`);
    appendRestoreStructureDetails(lines, folderStepResult, diagnosis);
  }

  return lines.join("\n");
}

function appendRestoreStructureDetails(
  lines: string[],
  result: RestoreStructureResult,
  diagnosis?: Diagnosis | null
): void {
  const moves = result.moves ?? [];
  const missingDocs: AuditDocRef[] = diagnosis?.missingFromTree ?? [];
  const externalIds = new Set<string>((diagnosis?.externalDocs ?? []).map((d) => d.id));
  const addedIds = new Set<string>(missingDocs.map((d) => d.id));
  const totalChanges = moves.length + missingDocs.length + externalIds.size;

  lines.push("");
  if (totalChanges === 0) {
    lines.push("No structural changes — project already matches the backup.");
    return;
  }

  // Strip added IDs from the move map — a missing doc "moving" from root to
  // its backup folder is really an addition, not a move.
  const movedMap = new Map<string, { from: string }>();
  for (const m of moves) if (!addedIds.has(m.id)) movedMap.set(m.id, { from: m.from });

  const tree = result.tree ?? [];
  const rootDocs = result.rootDocs ?? [];

  const summary: string[] = [];
  if (movedMap.size > 0) summary.push(`${movedMap.size} move${movedMap.size === 1 ? "" : "s"}`);
  if (addedIds.size > 0) summary.push(`${addedIds.size} to restore`);
  if (externalIds.size > 0) summary.push(`${externalIds.size} to remove`);
  lines.push(`Structure after repair — ${summary.join(" · ")}`);
  lines.push(`Legend: ➕ added   ✕ removed   ← moved`);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderNode = (node: any, depth: number) => {
    const indent = "  ".repeat(depth + 1);
    const isFolder = Array.isArray(node?.children);
    const added = !isFolder && addedIds.has(node.id);
    const external = !isFolder && !added && externalIds.has(node.id);
    const moved = !added && !external && movedMap.get(node.id);
    const icon = isFolder ? "📁" : added ? "➕" : external ? "✕" : "📄";
    const suffix = added
      ? "  (will be restored)"
      : external
      ? "  (will be removed)"
      : moved
      ? `  ← moved from ${moved.from}`
      : "";
    lines.push(`${indent}${icon} ${node.name || node.title || "Untitled"}${suffix}`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (isFolder) node.children.forEach((child: any) => renderNode(child, depth + 1));
  };
  tree.forEach((folder) => renderNode(folder, 0));
  rootDocs.forEach((doc) => renderNode(doc, 0));

  if ((result.unmatched ?? 0) > 0) {
    lines.push("");
    lines.push(`Unmatched documents (${result.unmatched} — not in backup, will stay at root):`);
    for (const d of result.unmatchedDocs ?? []) {
      lines.push(`  - ${d.title || "Untitled"} (${d.id})`);
    }
  }
}
